import { differenceInDays, format } from "date-fns"
import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React, { createContext, useEffect, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useMutation, useQuery } from "react-query"

import { queryProducts } from "src/api/Billing/Items"
import { getContactPaymentMethods } from "src/api/Contacts"
import {
  calculatePrice,
  getAvailability,
  getCouponCodes,
} from "src/api/TransientReservationWizard"

import useDebounce from "src/hooks/use_debounce"

import { centsToDollars } from "src/utils/UnitConversion"
import { snakecaseToCamelKeys } from "src/utils/object_helpers"

import {
  CUSTOM_RATE_ID,
  DEFAULT_RATE_ID,
  ELECTRIC_MONTHLY_BILLING_STRUCTURE,
  MONTHLY_BILLING_SCHEDULE,
  NO_COUPON_CODE_CHOSEN_VALUE,
  NO_ELECTRIC_CHOSEN_VALUE,
  NO_PRICING_STRUCTURE_CHOSEN_VALUE,
  STEPS,
} from "./constants"
import {
  discountsErrorMessage,
  formatFullStayDiscounts,
  formatRate,
  getApplicableDiscountIdProps,
} from "./helpers"
import {
  billingSchedulePropTypes,
  couponCodePropTypes,
  electricProductPropTypes,
  ratePropTypes,
  storageProductPropTypes,
} from "./propTypes"

/* eslint-disable camelcase */

export const WizardContext = createContext({
  currentStep: STEPS.availability,
  stepEnabled: () => {},
  goToStep: () => {},
  handleArrivalChange: () => {},
  handleDepartureChange: () => {},
  storageProducts: [],
  waitlistPath: "",
})

const WizardContextProvider = ({
  children,
  initialStep = STEPS.availability,
  billingSchedules,
  storageProducts,
  electricProducts,
  rateOptions,
  pricingStructures,
  couponCodes,
  waitlistPath,
  promoCodesPath,
  marinaSlug,
  handleOpenDiscountsModal,
  handleRemoveDiscount,
  telescopeCtaPath,
  telescopeEnabled,
  manageContactsPath,
  waitlistId,
  loadingCreateReservation,
  errorCreatingReservation,
  reservationSettingsPath,
  manageElectricItemsPath,
  useNewTaxExperience = false,
  shortTermDefaultBillingSchedule,
  longTermDefaultBillingSchedule,
}) => {
  const {
    setValue,
    watch,
    trigger,
    formState: { touchedFields },
    clearErrors,
  } = useFormContext()
  const [
    arrival,
    departure,
    electricProductId,
    billingScheduleId,
    storageProductId,
    contactId,
    contact,
    contactBoatId,
    contactBoat,
    newBoatLOA,
    newBoatBeam,
    stripeCardId,
    discounts,
    promoCodeOrDiscountTemplateId,
    promoCodeOrDiscountTemplateType,
    rateId,
    ratePricingStructure,
    rateAmount,
    rateTax,
  ] = watch([
    "arrival",
    "departure",
    "electric_product_id",
    "billing_schedule_id",
    "storage_product_id",
    "contact_id",
    "contact",
    "contact_boat_id",
    "contact_boat",
    "newBoat.lengthOverallFeet",
    "newBoat.beamFeet",
    "stripe_card_id",
    "discounts",
    "promo_code_or_discount_template_id",
    "promo_code_or_discount_template_type",
    "rate.id",
    "rate.pricing_structure",
    "rate.amount",
    "rate.tax_rate",
  ])

  const storageProduct = useMemo(
    () => storageProducts.find((p) => p.id === storageProductId),
    [storageProducts, storageProductId]
  )

  const setInitialStep = () => {
    if (initialStep === STEPS.summary && (!contactId || !contactBoatId)) {
      // Edge case handling for potential bad props being passed in from the backend.
      // Initial step cannot be set to "summary" if the contact & boat are not present.
      return STEPS.contact
    }
    return initialStep
  }
  const [currentStep, setCurrentStep] = useState(setInitialStep())

  const handleArrivalChange = (dateSelection) => {
    setValue("arrival", dateSelection)
    trigger("arrival")
  }

  const handleDepartureChange = (dateSelection) => {
    setValue("departure", dateSelection)
    trigger("departure")
  }

  const stepEnabled = (step) => {
    if (loadingCreateReservation) {
      return false
    }

    if ([STEPS.availability, STEPS.contact].includes(step)) {
      return true
    } else if (step === STEPS.summary && contactId && contactBoatId) {
      return true
    } else {
      return false
    }
  }

  const goToStep = (step) => {
    if (stepEnabled(step)) {
      setCurrentStep(step)
    }
  }

  const {
    mutate: getAvailabilityData,
    isLoading: isLoadingAvailabilityData,
    isError: getAvailabilityDataError,
    data: availabilityData,
  } = useMutation(() => {
    const params = {
      check_in_date: arrival,
      check_out_date: departure,
      storage_product_id: storageProductId,
    }
    return getAvailability({ marinaSlug, params })
  })

  useEffect(() => {
    if (arrival && departure) {
      getAvailabilityData()
    }
  }, [arrival, departure, getAvailabilityData, storageProductId])

  const boatDimensionsForCalculatePrice = () => {
    if (contactBoatId) {
      return { beam: null, length_overall: null }
    }
    if (contactBoat?.length_overall > 0 || contactBoat?.beam > 0) {
      return {
        beam: Number(contactBoat.beam) || null,
        length_overall: Number(contactBoat.length_overall) || null,
      }
    }
    return { beam: newBoatBeam * 12, length_overall: newBoatLOA * 12 }
  }

  const {
    mutate: getPriceEstimate,
    isLoading: isLoadingPriceEstimate,
    isError: getPriceEstimateError,
    data: priceEstimateData,
    error: priceEstimateError,
  } = useMutation(() => {
    const missingDates = !arrival || !departure
    const missingPricingStructure =
      rateId === CUSTOM_RATE_ID &&
      ratePricingStructure === NO_PRICING_STRUCTURE_CHOSEN_VALUE
    if (missingDates || missingPricingStructure) {
      return Promise.resolve(null)
    }
    const [discountTemplateId, promoCodeId] = getApplicableDiscountIdProps(
      promoCodeOrDiscountTemplateId,
      promoCodeOrDiscountTemplateType
    )

    // eslint-disable-next-line camelcase
    const { beam, length_overall } = boatDimensionsForCalculatePrice()
    const billingSchedule = billingSchedules.find(
      ({ id }) => id === billingScheduleId
    )
    const params = {
      check_in_date: format(arrival, "yyyy-MM-dd"),
      check_out_date: format(departure, "yyyy-MM-dd"),
      storage_product_id: storageProductId,
      length_overall,
      beam,
      contact_id: contactId || null,
      contact_boat_id: contactBoatId || null,
      electric_product_id:
        electricProductId === NO_ELECTRIC_CHOSEN_VALUE
          ? null
          : electricProductId,
      billing_schedule: billingSchedule.schedule,
      fees_and_discounts: JSON.stringify(formatFullStayDiscounts(discounts)),
      promo_code_id: promoCodeId,
      discount_template_id: discountTemplateId,
    }

    if (rateId !== DEFAULT_RATE_ID) {
      params.rate = formatRate({
        id: rateId,
        pricingStructure: ratePricingStructure,
        amount: rateAmount,
        taxRate: rateTax,
      })
    }
    return calculatePrice({ marinaSlug, params })
  })

  const [debouncedGetPriceEstimate] = useDebounce(getPriceEstimate)

  useEffect(() => {
    debouncedGetPriceEstimate()
  }, [
    arrival,
    debouncedGetPriceEstimate,
    departure,
    storageProductId,
    newBoatLOA,
    newBoatBeam,
    contactBoat,
    contactBoatId,
    contactId,
    electricProductId,
    billingScheduleId,
    discounts,
    promoCodeOrDiscountTemplateId,
    rateId,
    ratePricingStructure,
    rateAmount,
    rateTax,
  ])

  const [isMonthlyBilling, setIsMonthlyBilling] = useState(false)

  useEffect(() => {
    const selectedBillingSchedule = billingSchedules.find(
      (billingSchedule) => billingSchedule.id === billingScheduleId
    )
    setIsMonthlyBilling(
      selectedBillingSchedule.schedule === MONTHLY_BILLING_SCHEDULE
    )
  }, [billingScheduleId, billingSchedules])

  useEffect(() => {
    // reset the electric product to "none" IF the marina chooses monthly billing AFTER selecting a non-monthly electric product
    if (isMonthlyBilling && electricProductId !== NO_ELECTRIC_CHOSEN_VALUE) {
      const electricProduct = electricProducts.find(
        (product) => product.id === electricProductId
      )
      const isMonthlyElectric =
        electricProduct.defaultPricingStructure ===
        ELECTRIC_MONTHLY_BILLING_STRUCTURE
      if (!isMonthlyElectric) {
        setValue("electric_product_id", NO_ELECTRIC_CHOSEN_VALUE)
      }
    }
  }, [isMonthlyBilling, electricProductId, electricProducts])

  useEffect(() => {
    // reset the rate IF the marina chooses monthly billing AFTER selecting a non-monthly or default rate
    if (!isMonthlyBilling) return

    const isMonthlyRate = ratePricingStructure.includes("month")
    if (!isMonthlyRate || rateId === DEFAULT_RATE_ID) {
      const firstMonthlyPricingStructure =
        pricingStructures.find((pricingStructure) =>
          pricingStructure.includes("month")
        ) || NO_PRICING_STRUCTURE_CHOSEN_VALUE
      if (rateId === CUSTOM_RATE_ID) {
        // reset ONLY the rate pricing structure IF using a custom rate with a non-monthly pricing structure
        setValue("rate.pricing_structure", firstMonthlyPricingStructure)
      } else {
        // reset the entire rate IF using a non-monthly named rate
        setValue("rate", {
          id: CUSTOM_RATE_ID,
          pricing_structure: firstMonthlyPricingStructure,
          amount: null,
          tax_rate: null,
        })
      }
    }
  }, [isMonthlyBilling])

  useEffect(() => {
    // When selecting a rate, set all the rate-related form values
    const rate = rateOptions.find((rate) => rate.id.toString() === rateId)
    clearErrors(["rate.amount", "rate.tax_rate"])

    if (rateId === DEFAULT_RATE_ID) {
      setValue("rate", {
        id: rateId,
        pricing_structure: NO_PRICING_STRUCTURE_CHOSEN_VALUE,
        amount: null,
        tax_rate: null,
      })
    } else if (rateId === CUSTOM_RATE_ID) {
      const firstPricingStructureOption = isMonthlyBilling
        ? pricingStructures.find((pricingStructure) =>
            pricingStructure.includes("month")
          )
        : pricingStructures[0]
      setValue("rate", {
        id: rateId,
        pricing_structure: firstPricingStructureOption,
        amount: null,
        tax_rate: null,
      })
    } else if (rate) {
      setValue("rate", {
        id: rate.id,
        pricing_structure: rate.mbmTranslatedPricingStructure,
        amount: centsToDollars(rate.amount),
        tax_rate: new Decimal(rate.taxRate || 0).mul(100).toNumber(),
      })
    }
  }, [rateId, rateOptions])

  useEffect(() => {
    /*
      This useEffect handles setting the default billing schedule based on the length of the reservation,
      derived from the arrival/departure input.
    */
    if (touchedFields.billing_schedule_id) {
      // If the user already interacted with the billing schedule field, do *not* change it on their behalf.
      return
    }

    if (differenceInDays(departure, arrival) > 30) {
      setValue("billing_schedule_id", longTermDefaultBillingSchedule.id)
    } else {
      setValue("billing_schedule_id", shortTermDefaultBillingSchedule.id)
    }
  }, [
    arrival,
    departure,
    longTermDefaultBillingSchedule.id,
    shortTermDefaultBillingSchedule.id,
  ])

  const {
    data: contactCards,
    isLoading: contactCardsLoading,
    isError: contactCardsError,
  } = useQuery(
    ["trw-contact-cards", marinaSlug, contact?.encodedId],
    () =>
      getContactPaymentMethods({ marinaSlug, contactId: contact?.encodedId }),
    {
      initialData: [],
      enabled: Boolean(contact?.encodedId),
    }
  )

  useEffect(() => {
    setValue("stripe_card_id", "")
  }, [contact])

  useEffect(() => {
    if (contactCards.length > 0 && stripeCardId === "") {
      setValue("stripe_card_id", contactCards[0].stripePaymentMethodId)
    }
  }, [contactCards, stripeCardId])

  useEffect(() => {
    clearErrors(["stripe_card_id"])
  }, [stripeCardId])

  const [discountsError, setDiscountsError] = useState(null)
  const isCustomRate = rateId !== DEFAULT_RATE_ID

  useEffect(() => {
    // If we switch between monthly/non-monthly billing, fully reset the discounts array & coupon code.
    setValue("discounts", [])
    setValue("promo_code_or_discount_template_id", NO_COUPON_CODE_CHOSEN_VALUE)
    setDiscountsError(null)
  }, [isMonthlyBilling])

  useEffect(() => {
    // If we switch between default/custom rates, fully reset coupon codes.
    setValue("promo_code_or_discount_template_id", NO_COUPON_CODE_CHOSEN_VALUE)
  }, [isCustomRate])

  useEffect(() => {
    if (!isMonthlyBilling) {
      const errorMessage = discountsErrorMessage({
        discounts,
        couponCodeApplied:
          promoCodeOrDiscountTemplateId !== NO_COUPON_CODE_CHOSEN_VALUE,
        electricItemSelected: electricProductId !== NO_ELECTRIC_CHOSEN_VALUE,
      })

      setDiscountsError(errorMessage)
    }
  }, [
    promoCodeOrDiscountTemplateId,
    discounts,
    discountsError,
    isMonthlyBilling,
    electricProductId,
  ])

  const {
    data: couponCodesData,
    isLoading: couponCodesLoading,
    isError: couponCodesError,
  } = useQuery(
    [
      "trw-coupon-codes",
      marinaSlug,
      contact?.encodedId,
      arrival,
      departure,
      electricProductId,
      storageProductId,
    ],
    () => {
      const formattedArrival = arrival ? format(arrival, "yyyy-MM-dd") : null
      const formattedDeparture = departure
        ? format(departure, "yyyy-MM-dd")
        : null
      const productIds = [electricProductId, storageProductId].filter(
        (id) => id !== NO_ELECTRIC_CHOSEN_VALUE
      )
      return getCouponCodes({
        marinaSlug,
        arrival: formattedArrival,
        departure: formattedDeparture,
        productIds,
        contactId: contact?.encodedId,
      })
    },
    {
      initialData: {
        discounts: [], // we aren't doing anything with this; this just mirrors what's returned by the endpoint
        promoCodes: couponCodes,
      },
      enabled: Boolean(contact?.encodedId),
      refetchOnWindowFocus: true, // the user might navigate to the settings and add a coupon code mid-form. So, we want to re-fetch when we return to this window to get the updated list.
    }
  )

  const { data: electricProductsData } = useQuery(
    ["trw-electric-products", marinaSlug],
    async () => {
      const response = await queryProducts({
        page: 1,
        marinaSlug,
        availability: ["reservation_electric"],
        includeChildren: true,
      })
      return snakecaseToCamelKeys(response)
    },
    {
      initialData: electricProducts,
      refetchOnWindowFocus: true, // the user might navigate to the settings and add an electric product mid-form. So, we want to re-fetch when we return to this window to get the updated list.
    }
  )

  return (
    <WizardContext.Provider
      value={{
        marinaSlug,
        currentStep,
        stepEnabled,
        goToStep,
        handleArrivalChange,
        handleDepartureChange,
        storageProducts,
        storageProduct,
        electricProducts: electricProductsData,
        rateOptions,
        pricingStructures,
        billingSchedules,
        waitlistPath,
        promoCodesPath,
        availabilityData,
        isLoadingAvailabilityData,
        getAvailabilityDataError,
        isLoadingPriceEstimate,
        getPriceEstimateError,
        priceEstimateData,
        priceEstimateError,
        isCustomRate,
        isMonthlyBilling,
        contactCards,
        contactCardsLoading,
        contactCardsError,
        openDiscountsModal: handleOpenDiscountsModal,
        discountsModalDisabled: isMonthlyBilling,
        handleRemoveDiscount,
        discountsError,
        couponCodes: couponCodesData?.promoCodes,
        couponCodesLoading,
        couponCodesError,
        defaultCouponCodeId: couponCodesData?.defaultPromoCodeId,
        telescopeCtaPath,
        telescopeEnabled,
        manageContactsPath,
        waitlistId,
        loadingCreateReservation,
        errorCreatingReservation,
        reservationSettingsPath,
        manageElectricItemsPath,
        useNewTaxExperience,
      }}
    >
      {children}
    </WizardContext.Provider>
  )
}

WizardContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  marinaSlug: PropTypes.string.isRequired,
  initialStep: PropTypes.oneOf(Object.values(STEPS)),
  billingSchedules: PropTypes.arrayOf(PropTypes.shape(billingSchedulePropTypes))
    .isRequired,
  storageProducts: PropTypes.arrayOf(PropTypes.shape(storageProductPropTypes))
    .isRequired,
  electricProducts: PropTypes.arrayOf(PropTypes.shape(electricProductPropTypes))
    .isRequired,
  rateOptions: PropTypes.arrayOf(PropTypes.shape(ratePropTypes)).isRequired,
  couponCodes: PropTypes.arrayOf(PropTypes.shape(couponCodePropTypes))
    .isRequired,
  waitlistPath: PropTypes.string.isRequired,
  promoCodesPath: PropTypes.string.isRequired,
  handleOpenDiscountsModal: PropTypes.func.isRequired,
  handleRemoveDiscount: PropTypes.func.isRequired,
  pricingStructures: PropTypes.arrayOf(PropTypes.string).isRequired,
  telescopeCtaPath: PropTypes.string.isRequired,
  telescopeEnabled: PropTypes.bool.isRequired,
  manageContactsPath: PropTypes.string.isRequired,
  waitlistId: PropTypes.string,
  loadingCreateReservation: PropTypes.bool.isRequired,
  errorCreatingReservation: PropTypes.bool.isRequired,
  reservationSettingsPath: PropTypes.string.isRequired,
  manageElectricItemsPath: PropTypes.string.isRequired,
  useNewTaxExperience: PropTypes.bool,
  shortTermDefaultBillingSchedule: PropTypes.shape(billingSchedulePropTypes)
    .isRequired,
  longTermDefaultBillingSchedule: PropTypes.shape(billingSchedulePropTypes)
    .isRequired,
}

export default WizardContextProvider
